import { useEffect, useRef, useState } from "react";
import type {
  LiveAmbiguityGroup,
  LivePresenceEntry,
  LiveState,
  LiveSurface,
  Slot,
} from "@cs2/contracts";
import { liveServerMessageSchema } from "@cs2/contracts";

/** Backoff-Stufen für Reconnect — deckt kurze Netzhänger genauso ab wie einen Server-Neustart. */
const reconnectDelaysMs = [500, 1000, 2000, 5000, 10_000];
const heartbeatIntervalMs = 20_000;

export interface LiveSessionHandle {
  connected: boolean;
  online: LivePresenceEntry[];
  liveState: LiveState | null;
  ambiguity: LiveAmbiguityGroup[];
  selectTactic: (tacticId: string) => void;
  resolveMatching: (assignments: Partial<Record<Slot, string | null>>) => void;
}

/**
 * Live-Präsenz für Quickview und Editor. `enabled` gate ist die bestätigte
 * Session — vor dem Login-Check gibt es keinen Grund, eine WS-Verbindung zu
 * versuchen. Fällt bei jedem Fehler/Close automatisch mit Backoff zurück in
 * einen Reconnect-Loop, statt dauerhaft getrennt zu bleiben.
 *
 * `surface` sagt dem Server, aus welcher Ansicht die Verbindung kommt: nur
 * `quickview` nimmt an der Captain-Steuerung und am Slot-Matching teil, eine
 * `editor`-Verbindung zählt ausschließlich für die Präsenzanzeige (ADR 0012).
 */
export function useLiveSession(
  surface: LiveSurface,
  enabled: boolean,
): LiveSessionHandle {
  const [connected, setConnected] = useState(false);
  const [online, setOnline] = useState<LivePresenceEntry[]>([]);
  const [liveState, setLiveState] = useState<LiveState | null>(null);
  const [ambiguity, setAmbiguity] = useState<LiveAmbiguityGroup[]>([]);
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    let attempt = 0;
    let reconnectTimer: number | undefined;
    let heartbeatTimer: number | undefined;

    function connect() {
      if (disposed) return;
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const socket = new WebSocket(
        `${protocol}//${window.location.host}/api/live/ws?surface=${surface}`,
      );
      socketRef.current = socket;
      socket.onopen = () => {
        attempt = 0;
        setConnected(true);
        heartbeatTimer = window.setInterval(() => {
          if (socket.readyState === WebSocket.OPEN)
            socket.send(JSON.stringify({ type: "heartbeat" }));
        }, heartbeatIntervalMs);
      };
      socket.onmessage = (event) => {
        let parsed: ReturnType<typeof liveServerMessageSchema.parse>;
        try {
          parsed = liveServerMessageSchema.parse(
            JSON.parse(event.data as string) as unknown,
          );
        } catch {
          return;
        }
        if (parsed.type === "presence-update") setOnline(parsed.online);
        else if (parsed.type === "live-state")
          setLiveState({
            tacticId: parsed.tacticId,
            slotAssignments: parsed.slotAssignments,
            captainOnline: parsed.captainOnline,
          });
        else if (parsed.type === "ambiguity") setAmbiguity(parsed.groups);
      };
      socket.onclose = () => {
        setConnected(false);
        if (heartbeatTimer !== undefined) window.clearInterval(heartbeatTimer);
        if (disposed) return;
        const delay =
          reconnectDelaysMs[Math.min(attempt, reconnectDelaysMs.length - 1)];
        attempt += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };
      socket.onerror = () => socket.close();
    }
    connect();
    return () => {
      disposed = true;
      if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer);
      if (heartbeatTimer !== undefined) window.clearInterval(heartbeatTimer);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [enabled, surface]);

  function send(message: unknown): void {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN)
      socket.send(JSON.stringify(message));
  }

  return {
    connected,
    online,
    liveState,
    ambiguity,
    selectTactic: (tacticId) => send({ type: "select-tactic", tacticId }),
    resolveMatching: (assignments) =>
      send({ type: "resolve-matching", assignments }),
  };
}
